// A live status line for the stat strip: "All systems operational · 99.87% uptime
// over the last 30 days", linking to the public status page.
//
// Honest by construction: it renders nothing on a failed fetch, a non-operational
// status, or uptime under 99%, so the marketing page never shows a bad or broken
// badge. The fetch is client-only and starts after first paint, and the row keeps
// a fixed minimum height, so layout shift stays 0.

'use client'

import { useEffect, useState } from 'react'

const STATUS_API = 'https://api.uptrack.app/api/status/uptrack'
const STATUS_PAGE = 'https://status.uptrack.app'
const MIN_UPTIME = 99

type StatusResponse = { data?: { overall_status?: string; uptime_percentage?: number } }

export function LiveStatus() {
  const [uptime, setUptime] = useState<number | null>(null)

  useEffect(() => {
    const ctrl = new AbortController()
    const timer = window.setTimeout(() => ctrl.abort(), 4000)
    const run = async () => {
      try {
        const res = await fetch(STATUS_API, { signal: ctrl.signal })
        if (!res.ok) return
        const body = (await res.json()) as StatusResponse
        const d = body.data
        if (d?.overall_status === 'operational' && typeof d.uptime_percentage === 'number' && d.uptime_percentage >= MIN_UPTIME) {
          setUptime(d.uptime_percentage)
        }
      } catch {
        // Aborted, offline or blocked: show nothing.
      } finally {
        window.clearTimeout(timer)
      }
    }
    const idle = window.requestIdleCallback ? window.requestIdleCallback(() => void run(), { timeout: 2500 }) : window.setTimeout(() => void run(), 300)
    return () => {
      ctrl.abort()
      window.clearTimeout(timer)
      if (window.cancelIdleCallback && typeof idle === 'number') window.cancelIdleCallback(idle)
      else window.clearTimeout(idle)
    }
  }, [])

  return (
    <div className="flex min-h-6 items-center justify-center">
      {uptime !== null && (
        <a
          href={STATUS_PAGE}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex min-h-6 items-center gap-2 rounded-sm text-sm text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <span className="size-2 rounded-full bg-status-up" aria-hidden="true" />
          All systems operational · {uptime.toFixed(2)}% uptime over the last 30 days
        </a>
      )}
    </div>
  )
}
